import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, CATEGORY_STYLES, PRIMARY_CATEGORIES } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { LogOut, LayoutGrid, User, Scissors, Tag, Star, QrCode, ExternalLink, Trash2, Plus, Eye, EyeOff } from "lucide-react";

const NAV = [
  { to: "/dashboard", end: true, label: "Genel", slug: "overview", icon: LayoutGrid },
  { to: "/dashboard/profile", label: "Profil", slug: "profile", icon: User },
  { to: "/dashboard/portfolio", label: "Portfolyo", slug: "portfolio", icon: Scissors },
  { to: "/dashboard/services", label: "Hizmetler", slug: "services", icon: Tag },
  { to: "/dashboard/reviews", label: "Yorumlar", slug: "reviews", icon: Star },
  { to: "/dashboard/qr", label: "QR Kod", slug: "qr", icon: QrCode },
];

export default function Dashboard() {
  const { user, barberSlug, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="theme-dash min-h-screen">
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
          <div className="text-[10px] tracking-[0.32em] text-neutral-500" data-testid="dash-brand">{BRAND.wordmark}</div>
            <span className="text-neutral-300">|</span>
            <div className="text-sm text-neutral-700" data-testid="dash-user">{user?.email}</div>
          </div>
          <div className="flex items-center gap-3">
            {barberSlug && (
              <a href={`/${barberSlug}`} target="_blank" rel="noreferrer" data-testid="dash-view-public" className="text-sm text-neutral-700 hover:text-black flex items-center gap-1">
                <ExternalLink size={14} /> Sayfamı gör
              </a>
            )}
            <button data-testid="dash-logout" onClick={handleLogout} className="text-sm text-neutral-700 hover:text-black flex items-center gap-1">
              <LogOut size={14} /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 md:flex gap-8">
        <nav className="md:w-56 md:sticky md:top-24 md:self-start mb-6 md:mb-0">
          <div className="flex md:flex-col gap-1 overflow-x-auto no-scrollbar" data-testid="dash-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.slug}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap transition-colors ${isActive ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"}`
                }
              >
                <n.icon size={15} /> {n.label}
              </NavLink>
            ))}
          </div>
        </nav>

        <main className="flex-1 min-w-0">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="profile" element={<ProfileEditor />} />
            <Route path="portfolio" element={<PortfolioManage />} />
            <Route path="services" element={<ServicesManage />} />
            <Route path="reviews" element={<ReviewsManage />} />
            <Route path="qr" element={<QRSection slug={barberSlug} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

/* ---------------- Overview / Analytics ---------------- */
function Metric({ label, value, testid }) {
  return (
    <div className="bg-white border border-neutral-200 p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="mt-2 font-editorial text-4xl">{value}</div>
    </div>
  );
}

function Overview() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/analytics/summary?days=${days}`).then((r) => setData(r.data));
  }, [days]);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Son {days} Gün</div>
          <h1 className="font-editorial text-4xl mt-1">Genel bakış</h1>
        </div>
        <div className="flex gap-1 border border-neutral-200" data-testid="range-switch">
          {[7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-4 py-2 text-sm ${days === d ? "bg-neutral-900 text-white" : "text-neutral-700"}`}
              data-testid={`range-${d}`}
            >
              {d} gün
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="text-neutral-500">Yükleniyor…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Profil görüntülenmesi" value={data.counts.profile_view} testid="metric-profile" />
            <Metric label="QR ziyareti" value={data.counts.qr_visit} testid="metric-qr" />
            <Metric label="WhatsApp yönlendirmesi" value={data.counts.whatsapp_click} testid="metric-whatsapp" />
            <Metric label="Yol tarifi tıklaması" value={data.counts.directions_click} testid="metric-directions" />
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-neutral-200 p-6" data-testid="top-haircut">
              <div className="text-xs uppercase tracking-widest text-neutral-500">En çok incelenen model</div>
              {data.top_haircut ? (
                <div className="mt-4 flex items-center gap-4">
                  <img src={data.top_haircut.image_url} alt="" className="w-20 h-24 object-cover" />
                  <div>
                    <div className="font-editorial text-2xl">{data.top_haircut.category}</div>
                    <div className="text-sm text-neutral-500">{data.top_haircut.title} · {data.top_haircut.code}</div>
                    <div className="mt-2 text-sm"><span className="font-semibold">{data.top_haircut.views}</span> görüntülenme</div>
                  </div>
                </div>
              ) : (
                <div className="mt-4 text-sm text-neutral-500">Henüz veri yok.</div>
              )}
            </div>

            <div className="bg-white border border-neutral-200 p-6" data-testid="sources-panel">
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">Trafik kaynakları</div>
              <div className="space-y-3">
                {["instagram", "google", "qr", "direct"].map((s) => {
                  const total = Object.values(data.sources).reduce((a, b) => a + b, 0) || 1;
                  const val = data.sources[s] || 0;
                  const pct = Math.round((val / total) * 100);
                  const label = { instagram: "Instagram", google: "Google", qr: "QR", direct: "Direct" }[s];
                  return (
                    <div key={s} data-testid={`source-${s}`}>
                      <div className="flex justify-between text-sm">
                        <span>{label}</span>
                        <span className="text-neutral-500">{val} · {pct}%</span>
                      </div>
                      <div className="mt-1 h-1.5 bg-neutral-100 relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-neutral-900" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-8 text-xs text-neutral-500">
            WhatsApp tıklamaları müşteri olduğu anlamına gelmez — bunlar sadece "WhatsApp yönlendirmesi" olarak izlenir.
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Profile ---------------- */
function ProfileField({ label, k, form, set, type = "text", placeholder = "" }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">{label}</span>
      <input
        type={type}
        value={form[k] ?? ""}
        onChange={(e) => set(k, e.target.value)}
        placeholder={placeholder}
        data-testid={`profile-${k}`}
        className="w-full px-4 py-3 bg-white border border-neutral-200 focus:outline-none focus:border-neutral-900 transition-colors"
      />
    </label>
  );
}

function ProfileEditor() {
  const [form, setForm] = useState(null);
  useEffect(() => {
    api.get("/barber/me").then((r) => setForm(r.data));
  }, []);
  if (!form) return <div className="text-neutral-500">Yükleniyor…</div>;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setHour = (i, k, v) => {
    const hrs = [...form.opening_hours];
    hrs[i] = { ...hrs[i], [k]: v };
    setForm({ ...form, opening_hours: hrs });
  };

  const save = async () => {
    const payload = {
      name: form.name, description: form.description, short_location: form.short_location,
      logo_url: form.logo_url,
      hero_url: form.hero_url, phone: form.phone, whatsapp: form.whatsapp,
      instagram: form.instagram, address: form.address,
      google_maps_url: form.google_maps_url,
      latitude: form.latitude ? Number(form.latitude) : null,
      longitude: form.longitude ? Number(form.longitude) : null,
      opening_hours: form.opening_hours, slug: form.slug,
      years_experience: form.years_experience ? Number(form.years_experience) : null,
      show_years_experience: !!form.show_years_experience,
      google_rating: form.google_rating ? Number(form.google_rating) : null,
    };
    try {
      const { data } = await api.patch("/barber/me", payload);
      setForm(data);
      toast.success("Kaydedildi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıt edilemedi");
    }
  };

  const Field = (props) => <ProfileField {...props} form={form} set={set} />;

  return (
    <div>
      <h1 className="font-editorial text-4xl mb-8">Profil</h1>
      <div className="space-y-5">
        <Field label="Studio adı" k="name" />
        <Field label="Kısa konum (hero altında görünür)" k="short_location" placeholder="Karaköy, İstanbul" />
        <div className="grid md:grid-cols-3 gap-5">
          <Field label="Yıllık deneyim" k="years_experience" type="number" placeholder="12" />
          <label className="flex items-center gap-2 md:col-span-2 mt-6 text-sm">
            <input
              type="checkbox"
              checked={!!form.show_years_experience}
              onChange={(e) => set("show_years_experience", e.target.checked)}
              data-testid="profile-show-years"
            />
            Yıllık deneyimi profilde göster
          </label>
        </div>
        <Field label="Google puanı (ör. 4.9) — opsiyonel" k="google_rating" placeholder="4.9" />
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Kısa açıklama</span>
          <textarea
            value={form.description ?? ""}
            onChange={(e) => set("description", e.target.value)}
            rows={3}
            data-testid="profile-description"
            className="w-full px-4 py-3 bg-white border border-neutral-200 focus:outline-none focus:border-neutral-900 transition-colors"
          />
        </label>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Hero görsel URL" k="hero_url" placeholder="https://..." />
          <Field label="Logo URL" k="logo_url" placeholder="https://..." />
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="URL uzantısı (slug)" k="slug" placeholder="noxen" />
          <Field label="Instagram kullanıcı adı" k="instagram" placeholder="noxenbarber" />
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Telefon" k="phone" />
          <Field label="WhatsApp (uluslararası, sadece rakam)" k="whatsapp" placeholder="905555555555" />
        </div>
        <Field label="Adres" k="address" />
        <div className="grid md:grid-cols-3 gap-5">
          <Field label="Google Maps yönlendirme URL" k="google_maps_url" />
          <Field label="Enlem" k="latitude" />
          <Field label="Boylam" k="longitude" />
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Çalışma saatleri</div>
          <div className="border border-neutral-200 divide-y divide-neutral-200 bg-white">
            {(form.opening_hours || []).map((h, i) => (
              <div key={h.day} className="grid grid-cols-12 items-center gap-2 px-4 py-3" data-testid={`hours-${h.day}`}>
                <div className="col-span-4 md:col-span-3 text-sm">{h.day}</div>
                <label className="col-span-4 md:col-span-3 flex items-center gap-2 text-xs text-neutral-500">
                  <input type="checkbox" checked={h.closed} onChange={(e) => setHour(i, "closed", e.target.checked)} /> Kapalı
                </label>
                <input disabled={h.closed} type="time" value={h.open} onChange={(e) => setHour(i, "open", e.target.value)} className="col-span-2 md:col-span-3 border border-neutral-200 px-2 py-1 text-sm disabled:bg-neutral-50" />
                <input disabled={h.closed} type="time" value={h.close} onChange={(e) => setHour(i, "close", e.target.value)} className="col-span-2 md:col-span-3 border border-neutral-200 px-2 py-1 text-sm disabled:bg-neutral-50" />
              </div>
            ))}
          </div>
        </div>

        <button data-testid="profile-save" onClick={save} className="bg-neutral-900 text-white px-6 py-3 hover:bg-neutral-800 transition-colors">Kaydet</button>
      </div>
    </div>
  );
}

/* ---------------- Portfolio ---------------- */
function PortfolioManage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", category: PRIMARY_CATEGORIES[0], style: CATEGORY_STYLES[PRIMARY_CATEGORIES[0]][0], description: "", image_url: "", featured: false });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/haircuts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setCategory = (cat) => {
    setForm({ ...form, category: cat, style: CATEGORY_STYLES[cat][0] });
  };

  const add = async () => {
    if (!form.title || !form.image_url) { toast.error("Başlık ve görsel gerekli"); return; }
    setBusy(true);
    try {
      await api.post("/haircuts", { title: form.title, style: form.style, description: form.description, image_url: form.image_url, featured: form.featured });
      setForm({ ...form, title: "", description: "", image_url: "", featured: false });
      await load();
      toast.success("Eklendi");
    } catch (e) { toast.error("Eklenemedi"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/haircuts/${id}`);
    load();
  };

  const editStyle = async (id, style) => {
    await api.patch(`/haircuts/${id}`, { style });
    load();
  };

  const toggleFeatured = async (h) => {
    await api.patch(`/haircuts/${h.id}`, { featured: !h.featured });
    load();
  };

  return (
    <div>
      <h1 className="font-editorial text-4xl mb-8">Portfolyo</h1>

      <div className="bg-white border border-neutral-200 p-6 mb-8">
        <div className="text-sm font-medium mb-4">Yeni model ekle</div>
        <div className="grid md:grid-cols-2 gap-4">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Başlık (ör. Klasik Low Taper)" data-testid="hc-title" className="px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.category} onChange={(e) => setCategory(e.target.value)} data-testid="hc-category" className="px-4 py-3 border border-neutral-200 bg-white focus:outline-none focus:border-neutral-900">
              {PRIMARY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} data-testid="hc-style" className="px-4 py-3 border border-neutral-200 bg-white focus:outline-none focus:border-neutral-900">
              {CATEGORY_STYLES[form.category].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Görsel URL (https://…)" data-testid="hc-image" className="md:col-span-2 px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Kısa açıklama (opsiyonel)" data-testid="hc-desc" className="md:col-span-2 px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <label className="flex items-center gap-2 text-sm text-neutral-700 md:col-span-2">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="hc-featured" />
            Öne çıkarılsın (Tümü seçildiğinde ilk gösterilenler arasında olsun)
          </label>
        </div>
        <button disabled={busy} onClick={add} data-testid="hc-add" className="mt-4 bg-neutral-900 text-white px-5 py-2.5 hover:bg-neutral-800 transition-colors flex items-center gap-2">
          <Plus size={16} /> Ekle
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="hc-grid">
        {items.map((h) => (
          <div key={h.id} className="bg-white border border-neutral-200" data-testid={`hc-item-${h.code}`}>
            <div className="ar-3-4 overflow-hidden bg-neutral-100 relative">
              <img src={h.image_url} alt={h.title} className="w-full h-full object-cover" />
              {h.featured && <div className="absolute top-2 left-2 bg-white text-[10px] tracking-widest px-2 py-0.5">ÖNE ÇIKAN</div>}
            </div>
            <div className="p-3">
              <div className="text-[10px] tracking-widest text-neutral-500">{h.code} · {h.category}</div>
              <div className="text-sm font-medium truncate">{h.title}</div>
              <select value={h.style || ""} onChange={(e) => editStyle(h.id, e.target.value)} className="mt-2 w-full text-xs border border-neutral-200 px-2 py-1 bg-white">
                {Object.entries(CATEGORY_STYLES).map(([cat, styles]) => (
                  <optgroup key={cat} label={cat}>
                    {styles.map((s) => <option key={s} value={s}>{s}</option>)}
                  </optgroup>
                ))}
              </select>
              <div className="mt-2 flex gap-2">
                <button onClick={() => toggleFeatured(h)} className="flex-1 text-xs text-neutral-700 border border-neutral-200 hover:bg-neutral-50 py-1.5 transition-colors" data-testid={`hc-feat-${h.code}`}>
                  {h.featured ? "Öne çıkarmayı kaldır" : "Öne çıkar"}
                </button>
                <button onClick={() => del(h.id)} data-testid={`hc-del-${h.code}`} className="flex items-center justify-center gap-1 text-xs text-red-600 hover:bg-red-50 py-1.5 px-3 transition-colors">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Services ---------------- */
function ServicesManage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", price: "" });
  const load = () => api.get("/services").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.price) return;
    await api.post("/services", { name: form.name, price: Number(form.price) });
    setForm({ name: "", price: "" });
    load();
  };
  const update = async (id, k, v) => {
    await api.patch(`/services/${id}`, { [k]: k === "price" ? Number(v) : v });
    load();
  };
  const del = async (id) => { await api.delete(`/services/${id}`); load(); };

  return (
    <div>
      <h1 className="font-editorial text-4xl mb-8">Hizmetler</h1>
      <div className="bg-white border border-neutral-200 p-6 mb-6">
        <div className="grid md:grid-cols-3 gap-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Hizmet adı" data-testid="svc-name" className="px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Fiyat (₺)" type="number" data-testid="svc-price" className="px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <button onClick={add} data-testid="svc-add" className="bg-neutral-900 text-white px-5 py-2.5 hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2">
            <Plus size={16} /> Ekle
          </button>
        </div>
      </div>
      <div className="bg-white border border-neutral-200 divide-y divide-neutral-200" data-testid="svc-list">
        {items.map((s) => (
          <div key={s.id} className="grid grid-cols-12 items-center gap-3 px-4 py-3" data-testid={`svc-row-${s.id}`}>
            <input defaultValue={s.name} onBlur={(e) => update(s.id, "name", e.target.value)} className="col-span-6 border-b border-transparent focus:border-neutral-300 outline-none py-1" />
            <input defaultValue={s.price} type="number" onBlur={(e) => update(s.id, "price", e.target.value)} className="col-span-3 border-b border-transparent focus:border-neutral-300 outline-none py-1 text-right" />
            <div className="col-span-1 text-neutral-500 text-sm">₺</div>
            <button onClick={() => del(s.id)} className="col-span-2 text-red-600 text-xs hover:bg-red-50 py-1 flex items-center justify-center gap-1" data-testid={`svc-del-${s.id}`}>
              <Trash2 size={12} /> Sil
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Reviews ---------------- */
function ReviewsManage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ customer_name: "", rating: 5, text: "" });
  const load = () => api.get("/reviews").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.customer_name || !form.text) return;
    await api.post("/reviews", form);
    setForm({ customer_name: "", rating: 5, text: "" });
    load();
  };
  const toggleHide = async (r) => { await api.patch(`/reviews/${r.id}`, { hidden: !r.hidden }); load(); };
  const del = async (id) => { await api.delete(`/reviews/${id}`); load(); };

  return (
    <div>
      <h1 className="font-editorial text-4xl mb-8">Yorumlar</h1>
      <div className="bg-white border border-neutral-200 p-6 mb-6">
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Müşteri adı" data-testid="rev-name" className="px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <select value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} data-testid="rev-rating" className="px-4 py-3 border border-neutral-200 bg-white">
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} yıldız</option>)}
          </select>
          <button onClick={add} data-testid="rev-add" className="bg-neutral-900 text-white px-5 py-2.5 hover:bg-neutral-800 transition-colors">Ekle</button>
        </div>
        <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Yorum" rows={3} data-testid="rev-text" className="w-full px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
      </div>
      <div className="space-y-3" data-testid="rev-list">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-neutral-200 p-5" data-testid={`rev-item-${r.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="font-medium">{r.customer_name}</div>
                <div className="flex">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} size={13} className="fill-neutral-900 text-neutral-900" />
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => toggleHide(r)} className="text-xs text-neutral-600 flex items-center gap-1 hover:text-black" data-testid={`rev-toggle-${r.id}`}>
                  {r.hidden ? <><Eye size={13}/> Göster</> : <><EyeOff size={13}/> Gizle</>}
                </button>
                <button onClick={() => del(r.id)} className="text-xs text-red-600 flex items-center gap-1" data-testid={`rev-del-${r.id}`}>
                  <Trash2 size={13} /> Sil
                </button>
              </div>
            </div>
            <p className="text-sm text-neutral-700">{r.text}</p>
            <div className="mt-2 text-xs text-neutral-400">{new Date(r.date).toLocaleDateString("tr-TR")} {r.hidden && "· Gizli"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- QR ---------------- */
function QRSection({ slug }) {
  const url = `${window.location.origin}/${slug}?ref=qr`;
  const download = () => {
    const svg = document.getElementById("qr-svg");
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: "image/svg+xml" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${slug}-qr.svg`;
    link.click();
  };

  return (
    <div>
      <h1 className="font-editorial text-4xl mb-8">QR Kod</h1>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-neutral-200 p-6">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">QR kodun</div>
          <div className="flex items-center justify-center p-6 bg-white" data-testid="qr-code">
            <QRCodeSVG id="qr-svg" value={url} size={240} bgColor="#FFFFFF" fgColor="#111111" level="M" />
          </div>
          <div className="mt-4 text-xs text-neutral-500 break-all" data-testid="qr-url">{url}</div>
          <button onClick={download} data-testid="qr-download" className="mt-4 bg-neutral-900 text-white px-5 py-2.5 hover:bg-neutral-800 transition-colors">SVG olarak indir</button>
        </div>

        <div className="bg-[#111111] text-white p-8 flex flex-col items-center justify-center border border-neutral-800" data-testid="qr-print">
          <div className="text-[10px] tracking-[0.32em] text-[#C4A661] mb-6">ATLAS · BARBER · STUDIO</div>
          <div className="font-editorial text-2xl md:text-3xl text-center leading-snug">Saç modelinde kararsız mısınız?</div>
          <div className="text-neutral-400 text-sm mt-2 mb-8">Çalışmalarımızı inceleyin.</div>
          <div className="p-4 bg-white">
            <QRCodeSVG value={url} size={180} bgColor="#FFFFFF" fgColor="#111111" level="M" />
          </div>
          <div className="mt-8 text-xs tracking-widest text-neutral-500">Kamerayı QR'a doğrultun</div>
        </div>
      </div>
    </div>
  );
}
